import React, { useEffect, useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CPInput } from "../CPInput";
import { useOrganization } from "../../context/OrganizationContext";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { AdmSwitchRow } from "./desktop/AdmKit";
import "./adminDesktop.css";
import {
  PAYROLL_WEEK_START_OPTIONS,
  buildPayrollSettingsExample,
  payrollSettingsFormFromSettings,
  payrollSettingsFormToPatch,
  validatePayrollSettingsForm,
} from "../../helpers/adminPayrollSettingsDraft";

function PayrollSettingsExampleCard({ form }) {
  const example = useMemo(() => buildPayrollSettingsExample(form), [form]);

  return (
    <div className="rb-card" style={{ marginTop: "16px" }}>
      <div className="section-heading">EXAMPLE CALCULATION</div>

      <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
        Read-only preview for administrator understanding. Not connected to
        live payroll data.
      </p>

      <div style={{ display: "grid", gap: "10px", marginBottom: "16px" }}>
        <div>
          <div style={{ color: "rgba(255,255,255,0.62)", fontSize: "0.85rem" }}>
            Current Settings
          </div>
          <div style={{ marginTop: "6px", lineHeight: 1.6 }}>
            Week Starts On: {example.weekStartsOnLabel}
            <br />
            Weekly Overtime:{" "}
            {example.weeklyOvertimeEnabled ? "Enabled" : "Disabled"}
            <br />
            Threshold: {example.thresholdHours} Hours
            <br />
            Multiplier: {example.multiplier.toFixed(1)}×
          </div>
        </div>

        <div
          style={{
            height: "1px",
            background: "rgba(201, 168, 76, 0.18)",
            margin: "4px 0",
          }}
        />

        <div>
          <div style={{ color: "rgba(255,255,255,0.62)", fontSize: "0.85rem" }}>
            Example
          </div>
          <div style={{ marginTop: "6px", lineHeight: 1.6 }}>
            Employee works {example.exampleHours} hours at $
            {example.exampleRate.toFixed(2)}/hour
            <br />
            Regular Hours: {example.regularHours}
            <br />
            Overtime Hours: {example.overtimeHours}
            <br />
            Gross Pay: ${example.grossPay.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminPayrollSettingsScreen({ onBack }) {
  const { settings, updateSettings, loading, error } = useOrganization();
  const isDesktop = useIsDesktopViewport();
  const [form, setForm] = useState(() => payrollSettingsFormFromSettings(settings));
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    setForm(payrollSettingsFormFromSettings(settings));
  }, [settings]);

  const handleFieldChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setValidationErrors((current) => ({
      ...current,
      [field]: undefined,
    }));
  };

  const handleSave = async () => {
    const validation = validatePayrollSettingsForm(form);

    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }

    setSaving(true);
    try {
      await updateSettings(payrollSettingsFormToPatch(form));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CrewPilotModuleShell
      title="Payroll Settings"
      subtitle="Payroll week and weekly overtime configuration"
      sectionLabel="Payroll"
      onBack={onBack}
      layout={PAGE_LAYOUT.FORM}
    >
      <div className="adm-split">
      <div className="adm-stack">
      <div className="rb-card">
        <div className="section-heading">PAYROLL WEEK</div>

        {loading && (
          <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
            Loading payroll settings…
          </p>
        )}

        {error && (
          <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
            Database sync unavailable. Changes are cached locally until online.
          </p>
        )}

        <fieldset
          className="adm-radios"
          style={{
            border: "none",
            margin: 0,
            padding: 0,
            marginBottom: "20px",
          }}
        >
          <legend
            className="admin-field-label adm-legend"
            style={{ marginBottom: "10px", padding: 0 }}
          >
            Week Starts On
          </legend>

          {PAYROLL_WEEK_START_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={isDesktop ? "adm-radio" : "admin-toggle-row"}
              style={{ cursor: "pointer" }}
            >
              <input
                type="radio"
                name="payroll-week-starts-on"
                value={option.value}
                checked={form.weekStartsOn === option.value}
                onChange={() => handleFieldChange("weekStartsOn", option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>

        <div className="section-heading adm-subheading">WEEKLY OVERTIME</div>

        {isDesktop ? (
          <AdmSwitchRow
            title="Enable Weekly Overtime"
            checked={form.weeklyOvertimeEnabled}
            onChange={(value) => handleFieldChange("weeklyOvertimeEnabled", value)}
          />
        ) : (
        <label className="admin-toggle-row">
          <input
            type="checkbox"
            checked={form.weeklyOvertimeEnabled}
            onChange={(event) =>
              handleFieldChange("weeklyOvertimeEnabled", event.target.checked)
            }
          />
          <span>Enable Weekly Overtime</span>
        </label>
        )}
        <div className="adm-fields" style={isDesktop ? { marginTop: "16px" } : undefined}>

        <CPInput
          label="Threshold Hours"
          type="number"
          min="0.01"
          step="0.01"
          inputMode="decimal"
          value={form.thresholdHours}
          onChange={(value) => handleFieldChange("thresholdHours", value)}
        />
        {validationErrors.thresholdHours && (
          <p className="admin-placeholder-note" style={{ color: "#fca5a5" }}>
            {validationErrors.thresholdHours}
          </p>
        )}

        <CPInput
          label="Multiplier"
          type="number"
          min="1"
          step="0.1"
          inputMode="decimal"
          value={form.multiplier}
          onChange={(value) => handleFieldChange("multiplier", value)}
        />
        {validationErrors.multiplier && (
          <p className="admin-placeholder-note" style={{ color: "#fca5a5" }}>
            {validationErrors.multiplier}
          </p>
        )}

        </div>

        <div className="adm-actions">
        <button
          type="button"
          className="kpi-back-btn adm-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save payroll settings"}
        </button>

        {saved && (
          <p className="admin-save-confirmation" role="status">
            Payroll settings updated.
          </p>
        )}
        </div>
      </div>
      </div>

      <div className="adm-stack">
      <PayrollSettingsExampleCard form={form} />

      <div className="rb-card" style={{ marginTop: "16px" }}>
        <div className="section-heading">COMING SOON</div>
        <p className="admin-placeholder-note">
          Daily overtime, double time, statutory holiday rules, provincial
          presets, and CCQ-specific payroll rules are planned for future
          releases.
        </p>
      </div>
      </div>
      </div>
    </CrewPilotModuleShell>
  );
}
